import {physicalWallRuns,wallEdgeRuns} from '@artemis-group/aiw-contracts';
import {roomIncluded,type ScopeRoom} from './plan-scope.ts';

type Pt={x:number;y:number};
type Floor={walls:{a:Pt;b:Pt;thickness_m:number}[];doors:{from:Pt;to:Pt}[];open_edges:{a:Pt;b:Pt}[];
 robes:{points:Pt[]}[];rooms:(ScopeRoom&{shape:Pt[]})[];
 skirting?:{rooms:{area_id:string;name:string;skirting_m:number;scotia_m:number}[]}};
export type ProposalWallEdge={kind:'skirting'|'scotia';room:string;a:Pt;b:Pt};
const mm=(p:Pt):Pt=>({x:p.x*1000,y:p.y*1000});
const metres=(p:Pt):Pt=>({x:p.x/1000,y:p.y/1000});

/** Render only the frozen Proposal geometry. Its saved quantities are never recalculated or changed.
 * Plan coordinates are metres; the native helper operates in canvas points, so use one point per mm.
 */
export function proposalWallRuns(floor:Floor):{walls:{a:Pt;b:Pt;thickness:number}[];edges:ProposalWallEdge[]}{
 const walls=(floor.walls??[]).map((w,i)=>({id:String(i),a:mm(w.a),b:mm(w.b),thicknessMM:w.thickness_m*1000}));
 const wallDoors=(floor.doors??[]).flatMap(d=>{
  const a=mm(d.from),b=mm(d.to),width=Math.hypot(b.x-a.x,b.y-a.y);if(width<1)return [];
  const c={x:(a.x+b.x)/2,y:(a.y+b.y)/2};
  // Door identity was resolved when this frozen plan was made. Recover its nearest supporting wall,
  // never every nearby parallel wall, because the flattened plan has no native wall UUIDs.
  const candidates=walls.flatMap(w=>{
   const dx=w.b.x-w.a.x,dy=w.b.y-w.a.y,L=Math.hypot(dx,dy);if(L<1)return [];
   if(Math.abs(dx/L*(b.y-a.y)/width-dy/L*(b.x-a.x)/width)>0.01)return [];
   const at=((c.x-w.a.x)*dx+(c.y-w.a.y)*dy)/L;
   const distance=Math.abs((c.x-w.a.x)*dy-(c.y-w.a.y)*dx)/L;
   return distance<=150&&at>=-1&&at<=L+1?[{w,at,distance}]:[];
  }).sort((a,b)=>a.distance-b.distance);
  const hit=candidates[0];return hit?[{wallID:hit.w.id,posMM:hit.at,widthMM:width}]:[];
 });
 // Older frozen plans placed robe fronts only in robes[], dropping their open_edges entry. Only
 // polygon edges beside a room boundary can match the wall helper; recess backs stay outside it.
 const robeEdges=(floor.robes??[]).flatMap(r=>r.points.map((a,i)=>({a:mm(a),b:mm(r.points[(i+1)%r.points.length]!)})));
 const native={scaleMmPerPx:1,walls,wallDoors,openEdges:[...(floor.open_edges??[]).map(e=>({a:mm(e.a),b:mm(e.b)})),...robeEdges]};
 const edges=(floor.rooms??[]).filter(roomIncluded).flatMap(room=>{
  const held=floor.skirting?.rooms.find(r=>r.area_id===room.id||(!r.area_id&&r.name===room.name));
  const kinds:ProposalWallEdge['kind'][]=[];
  if((held?.skirting_m??0)>0)kinds.push('skirting');
  if((held?.scotia_m??0)>0)kinds.push('scotia');
  // The stored plan rounds doors/walls to millimetres while room points retain precision. Suppress
  // sub-millimetre fragments at shared jambs; they are rounding noise, never a board segment.
  return kinds.flatMap(kind=>wallEdgeRuns(native,{boundary:room.shape.map(mm)}).filter(run=>Math.hypot(run.b.x-run.a.x,run.b.y-run.a.y)>=1).map(run=>({kind,room:room.id,a:metres(run.a),b:metres(run.b)})));
 });
 return {walls:physicalWallRuns(native).map(w=>({a:metres(w.a),b:metres(w.b),thickness:w.thickness/1000})),edges};
}
